import * as React from 'react';
import {Suspense, useEffect} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {NavLink, Outlet, useNavigate} from 'react-router-dom';
import {Aperture, LogOut} from 'lucide-react';
import {logout} from '../../components/AuthMenu';
import {OpsLayoutQuery} from './__generated__/OpsLayoutQuery.graphql';

const QUERY = graphql`
    query OpsLayoutQuery {
        viewer {
            id
            name
            image
            admin
        }
    }`;

const navClass = ({isActive}: { isActive: boolean }) =>
    `px-3 py-2 text-xs font-bold tracking-widest uppercase border-b-2 transition-colors ${
        isActive ? 'border-lumina-purple text-white' : 'border-transparent text-gray-400 hover:text-white'
    }`;

// The server already refuses /ops to non-admins; this redirect only covers
// client-side navigation after a session expires.
const OpsShell: React.FC = () => {
    const {viewer} = useLazyLoadQuery<OpsLayoutQuery>(QUERY, {});
    const navigate = useNavigate();

    useEffect(() => {
        if (!viewer?.admin) navigate('/', {replace: true});
    }, [viewer, navigate]);

    if (!viewer?.admin) return null;

    return (
        <div className="bg-lumina-dark min-h-screen text-white">
            <header className="border-b border-gray-800 bg-black/60 backdrop-blur">
                <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <a href="/" className="flex items-center gap-2">
                            <Aperture className="w-6 h-6 text-lumina-accent"/>
                            <span className="font-display font-bold tracking-widest">OPS</span>
                        </a>
                        <nav className="flex items-center gap-1">
                            <NavLink to="/ops/reservations" className={navClass}>Reservations</NavLink>
                            <NavLink to="/ops/customers" className={navClass}>Customers</NavLink>
                            <NavLink to="/ops/inventory" className={navClass}>Inventory</NavLink>
                            <NavLink to="/ops/owners" className={navClass}>Owners</NavLink>
                            <NavLink to="/ops/products" className={navClass}>Products</NavLink>
                            <NavLink to="/ops/engagements" className={navClass}>Engagements</NavLink>
                            <NavLink to="/ops/talent" className={navClass}>Talent</NavLink>
                            <NavLink to="/ops/devices" className={navClass}>Devices</NavLink>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-400">
                        {viewer.image && <img src={viewer.image} alt="" className="w-7 h-7 rounded-full"/>}
                        <span>{viewer.name}</span>
                        <button onClick={logout} className="hover:text-white" title="Sign out">
                            <LogOut className="w-4 h-4"/>
                        </button>
                    </div>
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-6 py-8">
                <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                    <Outlet/>
                </Suspense>
            </main>
        </div>
    );
};

const OpsLayout: React.FC = () => (
    <Suspense fallback={<div className="bg-lumina-dark min-h-screen"/>}>
        <OpsShell/>
    </Suspense>
);

export default OpsLayout;
